/*
 *
 * Copyright (c) 2025 Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Alexander Orlov ("CONFIDENTIAL INFORMATION").
 *
 * Use, reproduction, modification, or distribution of this code
 * is permitted only pursuant to a written license expressly granted
 * by Alexander Orlov.
 *
 * You shall not disclose such confidential information and shall use
 * it only in accordance with the terms of the written license agreement
 * you have entered into with Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft
 *
 */

import apiClient from './client';
import { APIResponse } from '@/types';

// Owner Reports API
export const reportsApi = {
  // Get account statement report
  async getAccountStatement(year: number, month: number, ownerId?: string): Promise<APIResponse<AccountStatementReportDTO>> {
    const params = new URLSearchParams({ year: year.toString(), month: month.toString() });
    if (ownerId) params.append('ownerId', ownerId);
    const response = await apiClient.get(`/reports/account-statement?${params}`);
    return response.data;
  },

  // Get annual report
  async getAnnualReport(year: number, ownerId?: string): Promise<APIResponse<AnnualReportDTO>> {
    const params = new URLSearchParams({ year: year.toString() });
    if (ownerId) params.append('ownerId', ownerId);
    const response = await apiClient.get(`/reports/annual?${params}`);
    return response.data;
  },

  // Platform Reports API (Admin only)

  // Get platform account statement report
  async getPlatformAccountStatement(year: number, month: number): Promise<APIResponse<PlatformAccountStatementReportDTO>> {
    const response = await apiClient.get(`/reports/platform/account-statement?year=${year}&month=${month}`);
    return response.data;
  },

  // Get platform annual report
  async getPlatformAnnualReport(year: number): Promise<APIResponse<PlatformAnnualReportDTO>> {
    const response = await apiClient.get(`/reports/platform/annual?year=${year}`);
    return response.data;
  },
};

// Type definitions to match backend DTOs

export interface AccountStatementReportDTO {
  transactions: AccountStatementTransactionDTO[];
  totalIncome: number;
  totalExpenses: number;
  netAmount: number;
  year: number;
  month: number;
  userId: string;
  generatedAt: string;
}

export interface AccountStatementTransactionDTO {
  id: string;
  date: string;
  description: string;
  amount: number;
  type: 'income' | 'expense' | 'toll' | 'fee';
  transactionId: string;
  category: string;
  vehicleId: string;
  vehicleName: string;
}

export interface AnnualReportDTO {
  totalIncome: number;
  totalExpenses: number;
  netIncome: number;
  totalTollCharges: number;
  totalServiceFees: number;
  vehicleCount: number;
  transactionCount: number;
  year: number;
  userId: string;
  generatedAt: string;
  monthlyBreakdown: MonthlyBreakdownDTO[];
  categoryBreakdown: CategoryBreakdownDTO[];
}

export interface MonthlyBreakdownDTO {
  month: number;
  income: number;
  expenses: number;
  net: number;
}

export interface CategoryBreakdownDTO {
  category: string;
  amount: number;
  percentage: number;
}

export interface PlatformAccountStatementReportDTO {
  transactions: PlatformAccountStatementTransactionDTO[];
  totalRevenue: number;
  totalExpenses: number;
  netAmount: number;
  totalCommissions: number;
  year: number;
  month: number;
  generatedAt: string;
}

export interface PlatformAccountStatementTransactionDTO {
  id: string;
  date: string;
  description: string;
  amount: number;
  type: 'commission' | 'fee' | 'expense' | 'refund';
  ownerId: string;
  ownerName: string;
  transactionId: string;
  category: string;
  vehicleId: string;
  vehicleName: string;
}

export interface PlatformAnnualReportDTO {
  totalRevenue: number;
  totalExpenses: number;
  netIncome: number;
  totalCommissions: number;
  totalServiceFees: number;
  totalProcessingFees: number;
  ownerCount: number;
  vehicleCount: number;
  transactionCount: number;
  year: number;
  generatedAt: string;
  monthlyBreakdown: PlatformMonthlyBreakdownDTO[];
  categoryBreakdown: CategoryBreakdownDTO[];
  topOwners: TopOwnerDTO[];
}

export interface PlatformMonthlyBreakdownDTO {
  month: number;
  revenue: number;
  expenses: number;
  net: number;
  commissions: number;
}

export interface TopOwnerDTO {
  ownerId: string;
  ownerName: string;
  totalCommissions: number;
  vehicleCount: number;
  transactionCount: number;
}